import React, { useEffect, useState } from 'react';
import Layout from 'Component/Layouts/layout';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  Alert,
} from '@mui/material';
import TableComponent from 'Component/Table';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchAllAvaiableTraineesForApproval,
  submitTraineeStatus,
  updateTrainingTask,
} from 'thunks/trainingThunks/thunk';
import { updateBatchStatus, updateTraineeStatus } from 'reducer/traineeApprovalSlice';
import { ROLES, AUTHORIZED_EMPLOYEES } from 'Constants/roles';
import useCredentials from 'hooks/useCredentials';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import useIsLearningRMCH from 'hooks/useIsLearningRMCH';

const BatchApproval = () => {
  const dispatch = useDispatch();
  const { id: batchId } = useParams();

  const [openDialog, setOpenDialog] = useState(false);
  const [openTraineeRejectDialog, setOpenTraineeRejectDialog] = useState(false);
  const [actionType, setActionType] = useState('');
  const [reason, setReason] = useState('');
  const [selectedActions, setSelectedActions] = useState({});
  const [selectedTrainee, setSelectedTrainee] = useState(null);
  const [batchStatus, setBatchStatus] = useState(null);
  const { userData: user } = useCredentials();
  const navigate = useNavigate();
  const {
    data: { trainees, trainingType, submittedBy },
    loading,
  } = useSelector((state) => state.traineeApprovalSlice);
  const submitedByCurrentUser = submittedBy.includes(String(user?.employeeId));
  const [stepToSend, setStepToSend] = useState(2);
  const { isRM, isCH } = useIsLearningRMCH();

  // Update stepToSend when trainingType loads from Redux (useState initial value uses empty string)
  useEffect(() => {
    setStepToSend(trainingType?.toLowerCase() === 'collection_training' ? 1 : 2);
  }, [trainingType]);
  useEffect(() => {
    // if (isCH) {
    //   return;
    // } else {
    //   if (AUTHORIZED_EMPLOYEES.includes(user?.employeeId)) {
    //     return;
    //   } else {
    //     navigate('/unauthorized');
    //   }
    // }
  }, [user, navigate, isCH]);
  useEffect(() => {
    if ((batchStatus || submitedByCurrentUser) && user?.employeeId) {
      dispatch(
        updateTrainingTask({
          approvalAuthority: Number(user?.employeeId),
          batchId,
          step: stepToSend,
        })
      );
    }
  }, [batchStatus, submitedByCurrentUser, dispatch, user?.employeeId, batchId, stepToSend]);

  const sortedTrainees = [...trainees].sort((a, b) => {
    if (a.allowed === b.allowed) return 0;
    return a.allowed ? 1 : -1;
  });

  const getRowStyle = (row) => {
    if (!row.allowed) {
      return {
        backgroundColor: '#ffebee',
        '&:hover': {
          backgroundColor: '#ffcdd2',
        },
      };
    }
    return {};
  };

  useEffect(() => {
    dispatch(fetchAllAvaiableTraineesForApproval({ batchId, role: 'CH', type: 'batch' }));
  }, [batchId, dispatch]);

  const handleAction = (type) => {
    setActionType(type);
    if (type === 'Reject') {
      setOpenDialog(true);
    } else {
      setOpenDialog(true);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setActionType('');
    setReason('');
  };

  const handleConfirm = async () => {
    if (actionType === 'Approve') {
      const payload = { batchId, status: true, reason: reason ?? null, trainees };
      try {
        await dispatch(submitTraineeStatus({ batchId, step: stepToSend, payload })).unwrap();
        dispatch(updateBatchStatus({ batchId, status: true }));
        setBatchStatus('approved');
        toast.success('Batch approved successfully!', { theme: 'colored' });
      } catch (error) {
        toast.error(`Failed to approve batch. Please try again.`, { theme: 'colored' });
      }
    } else {
      const updatedTrainees = trainees.map((trainee) => ({
        ...trainee,
        allowed: false,
        reason: reason,
      }));
      const payload = { batchId, status: false, reason: reason ?? null, trainees: updatedTrainees };
      try {
        await dispatch(submitTraineeStatus({ batchId, step: stepToSend, payload })).unwrap();
        dispatch(updateBatchStatus({ batchId, status: false }));
        setBatchStatus('rejected');
        toast.success('Batch Rejected successfully!', { theme: 'colored' });
      } catch (error) {
        toast.error(`Failed to reject batch. Please try again.`, { theme: 'colored' });
      }
    }
    handleCloseDialog();
  };

  const handleActionChange = (empId, action) => {
    if (action === 'Declined') {
      setSelectedTrainee(empId);
      setOpenTraineeRejectDialog(true);
    } else {
      dispatch(
        updateTraineeStatus({ empId, allowed: action === 'Allowed', reason: `${action} By CH.` })
      );
      setSelectedActions((prev) => ({ ...prev, [empId]: action }));
    }
  };

  const handleTraineeRejectSubmit = () => {
    if (selectedTrainee && reason) {
      dispatch(updateTraineeStatus({ empId: selectedTrainee, allowed: false, reason }));
      setSelectedActions((prev) => ({ ...prev, [selectedTrainee]: 'Declined' }));
      setOpenTraineeRejectDialog(false);
      setReason('');
      setSelectedTrainee(null);
    }
  };

  const handleCloseTraineeRejectDialog = () => {
    setOpenTraineeRejectDialog(false);
    setReason('');
    setSelectedTrainee(null);
  };

  const columns = [
    { label: 'Employee ID', field: 'empId' },
    { label: 'Employee Name', field: 'empName' },
    { label: 'Region', field: 'region' },
    { label: 'Branch', field: 'branch' },
    { label: 'Role', field: 'role' },
    { label: 'Approved', field: 'allowed' },
    { label: 'Reason', field: 'reason' },
    { label: 'Nomination Count', field: 'nominationCount' },
    {
      label: 'Action',
      field: 'allowed',
      render: (row) => {
        const prefilledAction = row.allowed ? 'Allowed' : 'Declined';

        return (
          <Select
            value={selectedActions[row.empId] || prefilledAction}
            onChange={(e) => handleActionChange(row.empId, e.target.value)}
            displayEmpty
            size="small"
            sx={{ width: 120 }}
          >
            <MenuItem value="Allowed">Allowed</MenuItem>
            <MenuItem value="Declined">Declined</MenuItem>
          </Select>
        );
      },
    },
  ];

  return (
    <Layout>
      <Box
        sx={{
          fontSize: '24px',
          fontWeight: 500,
          background: 'linear-gradient(to bottom, #E9FBE0 0%, #E6F0E8 100%)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 3,
        }}
      >
        {`Batch Approval for BatchID: ${batchId}`}
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-around', my: 2 }}>
        <Button
          variant="contained"
          color={batchStatus === 'approved' ? 'success' : 'primary'}
          onClick={() => handleAction('Approve')}
          disabled={batchStatus !== null || submitedByCurrentUser}
        >
          {batchStatus === 'approved' ? 'Approved' : 'Approve'}
        </Button>
        <Button
          variant="contained"
          color={batchStatus === 'rejected' ? 'error' : 'primary'}
          onClick={() => handleAction('Reject')}
          disabled={batchStatus !== null || submitedByCurrentUser}
        >
          {batchStatus === 'rejected' ? 'Rejected' : 'Reject'}
        </Button>
      </Box>
      {submitedByCurrentUser && (
        <Alert
          severity="info"
          sx={{
            mt: 2,
            mb: 2,
            '& .MuiAlert-message': {
              fontSize: '14px',
              fontWeight: 500,
            },
          }}
        >
          Batch approval has already been submitted
        </Alert>
      )}
      <Box mt={2}>
        {loading ? (
          <p>Loading...</p>
        ) : (
          <TableComponent
            tableData={sortedTrainees}
            columns={columns}
            tableName="TraineeList"
            count={sortedTrainees?.length}
            useWholeRowClicked={false}
            getRowStyle={getRowStyle}
            enableLocalSearch={true}
          />
        )}
      </Box>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {actionType === 'Approve' ? 'Approve Batch' : 'Reject Batch'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {actionType === 'Approve'
              ? 'Are you sure you want to approve this batch?'
              : 'Please provide a reason for rejecting this batch:'}
          </DialogContentText>
          {actionType === 'Reject' && (
            <TextField
              autoFocus
              margin="dense"
              id="reason"
              label="Reason"
              type="text"
              fullWidth
              variant="outlined"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleConfirm} autoFocus>
            {actionType === 'Approve' ? 'Approve' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openTraineeRejectDialog}
        onClose={handleCloseTraineeRejectDialog}
        aria-labelledby="trainee-reject-dialog-title"
        aria-describedby="trainee-reject-dialog-description"
      >
        <DialogTitle id="trainee-reject-dialog-title">Reject Trainee</DialogTitle>
        <DialogContent>
          <DialogContentText id="trainee-reject-dialog-description">
            Please provide a reason for rejecting this trainee:
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="trainee-reason"
            label="Reason"
            type="text"
            fullWidth
            variant="outlined"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTraineeRejectDialog}>Cancel</Button>
          <Button onClick={handleTraineeRejectSubmit} autoFocus>
            Reject Trainee
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
};

export default BatchApproval;
